import { quoteApiConnector } from 'apiConnector/quote';
import { addonManagementQuoteRequestApi } from 'apiConnector/quote/addonManagementQuote';
import type { QuoteResponseWithStatus } from 'apiConnector/quote/batchQuote';
import { BaseQueueEpicFactory } from 'epic/utils/queuing/baseQueueEpicFactory';
import type { NextWorkflowError } from 'models/NextWorkflowError';
import type { AddonManagementQuoteRequestPayload } from 'models/quote/AddonQuoteRequestPayload';
import type { QuoteAck } from 'models/quote/QuoteAck';
import { ofType } from 'redux-observable';
import { EMPTY, merge, NEVER, of } from 'rxjs';
import {
  catchError,
  delay,
  filter,
  ignoreElements,
  map,
  mergeMap,
  startWith,
  take,
  takeUntil,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import type { SpEpic } from 'shared/middlewares/epic/epicMiddleware.factory';
import type { Action } from 'state/actions';
import {
  addonManagementQuoteAcked,
  addonManagementQuoteExpired,
  addonManagementQuoteRequested,
  addonManagementQuoteRequestFailed,
  addonManagementQuoteResultFailed,
} from 'state/addonManagementForm/addonManagementForm.action';
import {
  addonManagementQuoteRemovedThunk,
  quoteResultReceivedThunk,
} from 'state/addonManagementForm/addonManagementForm.thunks';
import type { QuoteDetailsData } from 'state/quotes/details/details.model';
import type { QuoteResultStatus } from 'state/quotes/results/result.model';
import type { PriceEnhancement } from 'state/quotes/trades/trades.model';
import type { EpicWithThunk } from 'state/state.common';
import { addElements } from 'state/utils/array';
import { dateNow } from 'utils/core/now';
import { fatEventApiConnector } from 'utils/streaming/api';
import { addonManagementQuoteResult } from 'utils/streaming/messages';

export const MAX_QUEUE_SIZE = 1000000;

export type AddonManagementQuoteResult = Readonly<{
  priceEnhancement?: PriceEnhancement;
  pricingTime?: string;
  quoteDetails?: QuoteDetailsData;
  quoteExternalId?: string;
  quoteId: string;
  solveResult?: number;
  status: QuoteResultStatus;
  tradeExecutionWindowInMilliseconds?: number;
  rejections?: NextWorkflowError[];
}>;
export type AddonManagementQuoteResultWithTradeWindowDetails = AddonManagementQuoteResult &
  Readonly<{
    tradeExecutionWindowStartTime?: number;
  }>;

export class AddonManagementQueueEpicFactory extends BaseQueueEpicFactory<string> {
  public addFormToQueueOnPricingRequestedEpic(): SpEpic {
    return actions$ =>
      actions$.pipe(
        ofType('ADDON_MANAGEMENT_QUOTE_ADD_TO_QUEUE'),
        tap(action => {
          const formIds = Object.keys(action.formIdsToQuoteIds);
          if (formIds.length > 0) {
            this.addToQueue(...formIds);
          }
        }),
        map(action => addonManagementQuoteRemovedThunk(Object.keys(action.formIdsToQuoteIds))),
      );
  }

  public priceFormEpic(): EpicWithThunk {
    return (actions$, state$, { httpClient, selectors: { getRequestForQuotes } }) =>
      actions$.pipe(
        ofType('USER_CURRENT_RECEIVED'),
        take(1),
        mergeMap(() =>
          this.itemsToHandle$.pipe(
            withLatestFrom(state$),
            mergeMap(([formIds, state]) => {
              this.startHandling(...formIds);

              const requestForQuotes: AddonManagementQuoteRequestPayload = getRequestForQuotes(state, formIds);

              return of(dateNow()).pipe(
                mergeMap(_ =>
                  addonManagementQuoteRequestApi(httpClient)
                    .price(requestForQuotes)
                    .pipe(
                      mergeMap(quoteAckResponses => {
                        const actions: Action[] = [];
                        const { acceptedForms, failedForms } = this.getAcceptedAndFailedForms(
                          quoteAckResponses.quotes,
                          formIds,
                        );
                        if (acceptedForms.length > 0) {
                          actions.push(addonManagementQuoteAcked(acceptedForms));
                        }
                        if (failedForms.length > 0) {
                          this.completeHandling(...failedForms);
                          actions.push(addonManagementQuoteRequestFailed(failedForms.map(formId => ({ formId }))));
                        }

                        return actions;
                      }),
                      startWith(addonManagementQuoteRequested(formIds)),
                      catchError(() => {
                        this.completeHandling(...formIds);
                        return of(addonManagementQuoteRequestFailed(formIds.map(formId => ({ formId }))));
                      }),
                    ),
                ),
              );
            }),
          ),
        ),
      );
  }

  public abortQuoteEpic(): EpicWithThunk {
    return (actions$, _state$, { httpClient }) => {
      return actions$.pipe(
        ofType('ADDON_MANAGEMENT_QUOTE_ABORTED'),
        tap(({ formId }) => this.completeHandling(formId)),
        mergeMap(({ formId, quoteId }) => {
          return quoteApiConnector(httpClient)
            .cancelQuote({ negotiationId: formId, quoteId })
            .pipe(
              ignoreElements(),
              catchError(_ => EMPTY),
            );
        }),
      );
    };
  }

  public quoteResultReceivedEpic(): EpicWithThunk {
    return (
      actions$,
      state$,
      { oldHttpClient, frontLogger, streaming, selectors: { getAddonManagementFormIdByLastQuoteId } },
    ) => {
      const abortQuoteCountDown$ = (formId: string) =>
        actions$.pipe(
          ofType(
            'ADDON_MANAGEMENT_FILTER_CHANGED',
            'ADDON_MANAGEMENT_QUOTE_REQUESTED',
            'ADDON_MANAGEMENT_QUOTE_TRADE_REQUESTED',
            'FORM_ENRICHMENT_REQUESTED',
          ),
          filter(payload => {
            switch (payload.type) {
              case 'ADDON_MANAGEMENT_FILTER_CHANGED':
                return true;
              case 'ADDON_MANAGEMENT_QUOTE_REQUESTED':
                return payload.formIds.includes(formId);
              case 'ADDON_MANAGEMENT_QUOTE_TRADE_REQUESTED':
              case 'FORM_ENRICHMENT_REQUESTED':
                return payload.formId === formId;
              default:
                return false;
            }
          }),
        );

      return actions$.pipe(
        ofType('USER_CURRENT_RECEIVED'),
        take(1),
        mergeMap(() =>
          addonManagementQuoteResult(streaming).pipe(
            withLatestFrom(state$),
            mergeMap(([lightEvent, state]) =>
              fatEventApiConnector(oldHttpClient)
                .getFatEvent<AddonManagementQuoteResult>(lightEvent)
                .pipe(
                  mergeMap(response => {
                    if (!response) {
                      return [];
                    }
                    const formId = getAddonManagementFormIdByLastQuoteId(state, response.quoteId);
                    if (!formId) {
                      return [];
                    }
                    this.completeHandling(formId);

                    const quoteResult: AddonManagementQuoteResultWithTradeWindowDetails = {
                      ...response,
                      tradeExecutionWindowStartTime: dateNow(),
                    };
                    const result$ = of(quoteResultReceivedThunk(quoteResult.quoteId, quoteResult));
                    const expiry$ = response.tradeExecutionWindowInMilliseconds
                      ? of(addonManagementQuoteExpired(formId, response.quoteId)).pipe(
                          delay(response.tradeExecutionWindowInMilliseconds),
                          takeUntil(abortQuoteCountDown$(formId)),
                        )
                      : NEVER;

                    return merge(result$, expiry$);
                  }),
                  catchError((error: Error) => {
                    frontLogger.error(
                      `An error occurred inside addonManagementQueueEpics.quoteResultReceivedEpic (@FrontError}`,
                      error,
                    );

                    if (!lightEvent.quoteId) {
                      return EMPTY;
                    }

                    const formId = getAddonManagementFormIdByLastQuoteId(state, lightEvent.quoteId);
                    if (formId) {
                      this.completeHandling(formId);
                      return of(
                        addonManagementQuoteResultFailed(formId ?? '', lightEvent.quoteId, { message: error.message }),
                      );
                    }

                    return EMPTY;
                  }),
                ),
            ),
          ),
        ),
      );
    };
  }

  public cancelQuotePushBackEpic(): EpicWithThunk {
    return (actions$, state$, { httpClient, selectors: { getAddonManagementFormLastQuoteId } }) =>
      actions$.pipe(
        ofType('ADDON_MANAGEMENT_QUOTE_PUSH_BACK_CANCEL_REQUESTED'),
        withLatestFrom(state$),
        mergeMap(([{ formId }, state]) => {
          this.completeHandling(formId);

          const quoteId = getAddonManagementFormLastQuoteId(state, formId);

          if (!quoteId) {
            return EMPTY;
          }

          return quoteApiConnector(httpClient)
            .cancelQuote({ quoteId, negotiationId: formId })
            .pipe(
              map(() => addonManagementQuoteResultFailed(formId, quoteId, { message: '' })),
              catchError(() => {
                return EMPTY;
              }),
            );
        }),
      );
  }

  private getAcceptedAndFailedForms(
    quoteAckResponses: readonly QuoteResponseWithStatus[],
    formIds: readonly string[],
  ): FormResponse {
    return quoteAckResponses.reduce<FormResponse>(
      (result, res, index) => {
        switch (res.status) {
          case 'Acknowledged':
            return { ...result, acceptedForms: addElements(result.acceptedForms, res.acknowledgement) };
          case 'Failed':
            return { ...result, failedForms: addElements(result.failedForms, formIds[index]) };
          default:
            return result;
        }
      },
      {
        acceptedForms: [],
        failedForms: [],
      },
    );
  }
}

export const addonManagementQueueEpics = new AddonManagementQueueEpicFactory(
  'addonManagementQueueEpics',
  MAX_QUEUE_SIZE,
);

interface FormResponse {
  acceptedForms: readonly QuoteAck[];
  failedForms: readonly string[];
}
